import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, KeyRound, Check, X } from "lucide-react";

// Lightweight in-house password scorer — avoids pulling in zxcvbn (~400kb).
// Returns { score: 0-4, label, color, checks: [{label, ok}] }.
function scorePassword(pw) {
  const checks = [
    { label: "At least 8 characters", ok: pw.length >= 8 },
    { label: "Lower & uppercase letters", ok: /[a-z]/.test(pw) && /[A-Z]/.test(pw) },
    { label: "At least one number", ok: /\d/.test(pw) },
    { label: "At least one symbol", ok: /[^A-Za-z0-9]/.test(pw) },
    { label: "12+ characters (bonus)", ok: pw.length >= 12 },
  ];
  const passed = checks.filter((c) => c.ok).length;
  const hasLengthBonus = checks[4].ok; // 12+ chars
  // Map raw pass count → 0..4 score. "Strong" requires the 12+ bonus so
  // an 8-char password can't display as Strong even if it hits every other rule.
  let score = 0;
  if (pw.length === 0) score = 0;
  else if (passed <= 1) score = 1;
  else if (passed === 2) score = 2;
  else if (passed === 3) score = 3;
  else score = hasLengthBonus ? 4 : 3;
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["#3f3f46", "#ef4444", "#f59e0b", "#84cc16", "#10b981"];
  return { score, label: labels[score], color: colors[score], checks };
}

// Forced password-change screen for first-login cast users. Reached
// automatically from Login when `user.must_change_password === true`.
export default function ChangePassword() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const forced = !!user?.must_change_password;
  const strength = useMemo(() => scorePassword(next), [next]);
  // Block save until "Fair" or better + 8+ chars — matches backend rule.
  const tooWeak = next.length > 0 && strength.score < 2;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (tooWeak) return setError("Please choose a stronger password.");
    if (next !== confirm) return setError("The two new-password fields don't match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      // Refresh so must_change_password flips false before we route away.
      if (refresh) await refresh();
      nav("/productions", { replace: true });
    } catch (ex) {
      setError(formatApiErrorDetail(ex.response?.data?.detail) || "Couldn't update password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-5 py-16">
      <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="change-password-form">
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> StageCall
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight font-light">
          {forced ? "Set your permanent password" : "Change your password"}
        </h1>
        {forced && (
          <p className="text-sm text-zinc-400">Your temporary password is one-time-only. Pick something you'll remember — you can always reset it later from Settings.</p>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Current (temporary) password</Label>
            <Input type="password" autoComplete="current-password" data-testid="cp-current" required
              value={current} onChange={(e) => setCurrent(e.target.value)}
              className="mt-2 bg-[#121212] border-white/10 text-white h-11" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">New password</Label>
            <Input type="password" autoComplete="new-password" data-testid="cp-new" required
              value={next} onChange={(e) => setNext(e.target.value)}
              className="mt-2 bg-[#121212] border-white/10 text-white h-11" />
            {next.length > 0 && (
              <div className="mt-2 space-y-2" data-testid="cp-strength">
                <div className="flex gap-1" aria-label={`Password strength: ${strength.label}`}>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-1 flex-1 rounded-full transition-colors duration-200"
                      style={{ backgroundColor: i <= strength.score ? strength.color : "#27272a" }} />
                  ))}
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-zinc-400">Strength</span>
                  <span data-testid="cp-strength-label" className="font-medium" style={{ color: strength.color }}>
                    {strength.label}
                  </span>
                </div>
                <ul className="space-y-1 pt-1">
                  {strength.checks.map((c) => (
                    <li key={c.label} className="flex items-center gap-1.5 text-[11px]"
                      style={{ color: c.ok ? "#a1a1aa" : "#52525b" }}>
                      {c.ok ? <Check className="w-3 h-3 text-emerald-500" /> : <X className="w-3 h-3 text-zinc-600" />}
                      <span className={c.ok ? "line-through decoration-zinc-700" : ""}>{c.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {next.length === 0 && (
              <div className="text-[10px] text-zinc-500 mt-1">Minimum 8 characters.</div>
            )}
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Confirm new password</Label>
            <Input type="password" autoComplete="new-password" data-testid="cp-confirm" required
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 bg-[#121212] border-white/10 text-white h-11" />
          </div>
        </div>
        {error && <div data-testid="cp-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">{error}</div>}
        <button type="submit" disabled={busy || tooWeak || next.length < 8} data-testid="cp-submit"
          style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }}
          className="w-full h-11 rounded-md font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
          <KeyRound className="w-4 h-4" /> {busy ? "Saving…" : "Save new password"}
        </button>
      </form>
    </div>
  );
}
